import React from "react";
import { motion } from "framer-motion";
import { FaGithub } from "react-icons/fa";

export default function Projects() {
  const projects = [
    {
      title: "Job Board Portal",
      description:
        "Full-stack job portal enabling employers to post and manage jobs while candidates search, apply, and track applications. Built with role-based dashboards, JWT authentication, and PostgreSQL-backed REST APIs.",
      tech: ["React", "Node.js", "Express", "PostgreSQL", "JWT", "Tailwind CSS"],
      live: "https://job-board-seven-ashen.vercel.app/",
      github: "https://github.com/vinodpachipala1/JobBoard",
    },
    {
      title: "E-Commerce Platform",
      description:
        "Scalable marketplace supporting customers and sellers with product management, cart, and order workflows. Features role-based access, secure authentication, and dynamic filtering using React, Node.js, and PostgreSQL.",
      tech: ["React", "Node.js", "Express", "PostgreSQL", "JWT", "Tailwind CSS"],
      live: "https://ecommerce-theta-rose-83.vercel.app/",
      github: "https://github.com/vinodpachipala1/ecommerce",
    },
    {
      title: "Quiz Master",
      description:
        "Interactive quiz platform with timer-based attempts, real-time scoring, and performance analytics. Supports quiz creation and anonymous participation with backend evaluation logic.",
      tech: ["React", "Node.js", "Express", "MongoDB/PostgreSQL", "Tailwind CSS"],
      live: "https://quiz-master-ivory.vercel.app/",
      github: "https://github.com/vinodpachipala1/QuizMaster",
    },
    {
      title: "Expense Splitter",
      description:
        "Group expense management app with real-time balance tracking and bill splitting. Simplifies shared expenses through dynamic calculations and an intuitive UI.",
      tech: ["React", "Node.js", "Express", "PostgreSQL", "Tailwind CSS"],
      live: "https://expense-splitter-xi-two.vercel.app/",
      github: "https://github.com/vinodpachipala1/Expense-Splitter",
    },
  ];

  return (
    <motion.section
      id="projects"
      className="py-16 md:py-24 px-4 text-white relative"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
    >
      <div className="text-center mb-10 md:mb-16">
        <motion.h2
          className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent inline-block"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Projects
        </motion.h2>
        <motion.div
          className="h-1 w-20 md:w-24 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto mt-3 md:mt-4 rounded-full"
          initial={{ width: 0 }}
          whileInView={{ width: 96 }}
          transition={{ duration: 0.8 }}
        />
      </div>

      <motion.div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 lg:gap-8 max-w-[1400px] mx-auto"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={{
          visible: { transition: { staggerChildren: 0.1 } },
        }}
      >
        {projects.map((project, index) => (
          <motion.div
            key={index}
            className="group relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl md:rounded-2xl overflow-hidden border border-gray-700 hover:border-blue-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/20 hover:-translate-y-2"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-pink-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700 rounded-xl md:rounded-2xl pointer-events-none" />

            <div className="relative w-full h-40 md:h-52 overflow-hidden bg-gray-900 rounded-t-xl md:rounded-t-2xl">
              <a href={project.live} target="_blank" rel="noopener noreferrer" className="absolute inset-0 z-10">
                <iframe
                  src={project.live}
                  className="pointer-events-none absolute top-0 left-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                  title={project.title}
                />
              </a>
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent opacity-40" />
            </div>

            <div className="p-4 md:p-6 relative z-10">
              <h3 className="text-lg md:text-2xl font-bold mb-1 md:mb-2 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent group-hover:from-blue-400 group-hover:to-purple-400 transition-all duration-300">
                {project.title}
              </h3>

              <p className="text-gray-300 text-xs md:text-sm leading-relaxed mb-3 md:mb-4 line-clamp-3 md:line-clamp-none">
                {project.description}
              </p>

              <div className="flex flex-wrap gap-1.5 md:gap-2 mb-4 md:mb-5">
                {project.tech.map((tech, i) => (
                  <span
                    key={i}
                    className="text-[10px] md:text-xs px-2 md:px-2.5 py-0.5 md:py-1 bg-gray-800/80 text-gray-300 rounded-full border border-gray-700 group-hover:border-blue-500/30 transition-all duration-300"
                  >
                    {tech}
                  </span>
                ))}
              </div>

              <div className="flex items-center justify-between pt-2 md:pt-3 border-t border-gray-700 group-hover:border-blue-500/30 transition-colors">
                <a
                  href={project.live}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 text-xs md:text-sm font-medium inline-flex items-center gap-1 transition-all hover:gap-2 group/link"
                >
                  Live Demo
                  <span className="inline-block transition-transform group-hover/link:translate-x-1">→</span>
                </a>
                {project.github && (
                  <a
                    href={project.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-all hover:scale-110 hover:rotate-3"
                    aria-label="GitHub"
                  >
                    <FaGithub size={16} className="md:w-5 md:h-5" />
                  </a>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </motion.section>
  );
}